import { Box, BoxProps, Portal, usePopper } from '@chakra-ui/react'
import React, {
  useImperativeHandle,
  useLayoutEffect,
  useRef,
  useState,
} from 'react'
import { createPortal } from 'react-dom'

import { BOUNDARY_ATTRIBUTE } from '../../hooks/useClickOutsideBoundary'
import { useIsDark } from '../../hooks/useIsDark'

const POPOVER_CARD_ATTR = 'data-popover-card'

export type PopoverCardProps = BoxProps & {
  referenceElement: HTMLElement | null
  isOpen: boolean
  onClose: () => void
  children: React.ReactNode
  width?: string | number
  /** When set, clicks inside this popover are treated as inside the given click-outside boundary (e.g. filter editor). */
  clickBoundaryId?: string
  /** Optional ref assigned to the popover root (for outside-click checks). */
  contentRef?: React.Ref<HTMLDivElement | null>
  /** When set, render into this container instead of Portal so clicks are "inside" the container (e.g. chip popover). */
  containerRef?: React.RefObject<HTMLElement | null>
}

export const PopoverCard = ({
  referenceElement,
  isOpen,
  onClose,
  children,
  width,
  clickBoundaryId,
  contentRef,
  containerRef,
  ...props
}: PopoverCardProps) => {
  const isDark = useIsDark()
  const boxRef = useRef<HTMLDivElement>(null)
  const [hasContainer, setHasContainer] = useState(
    () => !!containerRef?.current,
  )
  const { popperRef, referenceRef } = usePopper({
    placement: 'bottom-start',
    strategy: 'fixed',
    modifiers: [
      {
        name: 'offset',
        options: {
          offset: [0, 6],
        },
      },
    ],
  })

  useImperativeHandle(referenceRef, () => referenceElement!)

  // When containerRef is provided, re-render once it's set (refs don't trigger re-renders).
  useLayoutEffect(() => {
    if (!containerRef?.current) return
    setHasContainer(true)
  }, [containerRef])

  // Set boundary and popover-card attributes on the DOM node so click-outside
  // logic always sees them (Chakra may not forward data-* on Box in some setups).
  useLayoutEffect(() => {
    const el = boxRef.current
    if (!el) return
    el.setAttribute(POPOVER_CARD_ATTR, '')
    if (clickBoundaryId) {
      el.setAttribute(BOUNDARY_ATTRIBUTE, clickBoundaryId)
    } else {
      el.removeAttribute(BOUNDARY_ATTRIBUTE)
    }
  }, [clickBoundaryId])

  const setRef = (node: HTMLDivElement | null) => {
    ;(boxRef as React.MutableRefObject<HTMLDivElement | null>).current = node
    if (typeof popperRef === 'function') {
      popperRef(node)
    } else if (popperRef) {
      ;(popperRef as React.MutableRefObject<HTMLDivElement | null>).current =
        node
    }
    if (contentRef != null) {
      if (typeof contentRef === 'function') {
        contentRef(node)
      } else {
        ;(contentRef as React.MutableRefObject<HTMLDivElement | null>).current =
          node
      }
    }
  }

  if (!isOpen) return null

  const boxContent = (
    <Box
      ref={setRef}
      width={width}
      borderRadius="4px"
      boxShadow={
        isDark
          ? '0 4px 24px 0 rgba(0, 0, 0, 0.24)'
          : '0 3px 10px 0 rgba(0, 0, 0, 0.05), 0 4px 3px 0 rgba(0, 0, 0, 0.03)'
      }
      bg={isDark ? 'presets.neutral.100' : 'presets.neutral.0'}
      color={isDark ? 'presets.neutral.0' : 'presets.neutral.90'}
      border={isDark ? '1px solid' : undefined}
      borderColor={isDark ? 'presets.neutral.90' : undefined}
      zIndex={1600}
      padding="16px"
      onMouseDown={e => e.stopPropagation()}
      onClick={e => e.stopPropagation()}
      {...props}
    >
      {children}
    </Box>
  )

  const container = hasContainer ? (containerRef?.current ?? null) : null
  if (container) {
    return createPortal(boxContent, container)
  }

  return <Portal>{boxContent}</Portal>
}
